import { KNOWLEDGE_ITEM_TYPES } from './knowledgeItemModel';

const STRUCTURED_TYPES = new Set([
  KNOWLEDGE_ITEM_TYPES.MEDICATION,
  KNOWLEDGE_ITEM_TYPES.GOAL,
]);

const TOPIC_STOP_WORDS = new Set([
  'a', 'an', 'and', 'are', 'at', 'be', 'during', 'for', 'from', 'has', 'have',
  'help', 'helps', 'helpful', 'is', 'it', 'no', 'not', 'now', 'of', 'on',
  'the', 'their', 'them', 'they', 'to', 'was', 'with', 'work', 'works',
]);

const GENERIC_CONTEXT_WORDS = new Set([
  'activity', 'afternoon', 'bedtime', 'day', 'evening', 'home', 'morning',
  'night', 'routine', 'school', 'time', 'transition', 'transitions', 'week',
]);

const NEGATING_UPDATE_PATTERN = /\b(?:no longer|not anymore|does(?:n['’]t| not)|is(?:n['’]t| not)|stopped|stop(?:s|ped)?|never)\b/i;

const normalizeText = (value = '') => String(value || '').toLowerCase().replace(/[^a-z0-9'’]+/g, ' ').trim();

const getFactText = (item = {}) => normalizeText([
  item.title,
  item.summary || item.content,
].filter(Boolean).join(' '));

export const findMatchingKnowledgeItem = (candidate = {}, existingItems = []) => {
  const candidateType = String(candidate.knowledgeType || candidate.factType || '').trim().toLowerCase();
  const candidateText = getFactText(candidate);
  if (!candidateText) return null;

  return existingItems.find((existingItem) => {
    if (!isKnowledgeConflictEligible(existingItem)) return false;
    const existingType = String(
      existingItem.knowledgeType || existingItem.factType || ''
    ).trim().toLowerCase();
    return (!candidateType || !existingType || candidateType === existingType)
      && getFactText(existingItem) === candidateText;
  }) || null;
};

const getTopicTokens = (item = {}) => new Set(
  getFactText(item)
    .split(/\s+/)
    .filter((token) => token.length > 2 && !TOPIC_STOP_WORDS.has(token))
);

const hasSharedTopic = (left = {}, right = {}) => {
  const leftTokens = getTopicTokens(left);
  const rightTokens = getTopicTokens(right);
  return [...leftTokens].some((token) => (
    rightTokens.has(token) && !GENERIC_CONTEXT_WORDS.has(token)
  ));
};

export const isKnowledgeConflictEligible = (item = {}) => {
  const knowledgeType = String(item.knowledgeType || item.factType || '').trim().toLowerCase();
  const chapterId = String(item.profileChapterId || '').trim().toLowerCase();
  return Boolean(item.id)
    && !STRUCTURED_TYPES.has(knowledgeType)
    && chapterId !== 'goals'
    && item.factStatus !== 'outdated'
    && item.status !== 'outdated'
    && item.status !== 'archived'
    && item.archived !== true
    && item.reviewState !== 'discarded'
    && item.reviewStatus !== 'discarded';
};

export const findLikelyKnowledgeConflict = (newItem = {}, existingItems = [], options = {}) => {
  if (STRUCTURED_TYPES.has(newItem.knowledgeType) || newItem.profileChapterId === 'goals') return null;
  const newText = getFactText(newItem);
  if (!newText) return null;
  const newIsNegatingUpdate = NEGATING_UPDATE_PATTERN.test(newText);

  return existingItems.find((existingItem) => {
    if (!isKnowledgeConflictEligible(existingItem)) return false;
    if (!hasSharedTopic(newItem, existingItem)) return false;
    const existingText = getFactText(existingItem);
    if (NEGATING_UPDATE_PATTERN.test(existingText) === newIsNegatingUpdate) return false;

    if (options.assumeSameDomain === true) return true;

    const newField = String(newItem.profileField || '').trim();
    const existingField = String(existingItem.profileField || '').trim();
    if (newField && existingField && newField !== existingField) return false;

    const newType = String(newItem.knowledgeType || '').trim();
    const existingType = String(existingItem.knowledgeType || existingItem.factType || '').trim();
    return !newType || !existingType || newType === existingType || Boolean(newField && existingField);
  }) || null;
};
